import React, { RefObject } from 'react';
import Chip from '@material-ui/core/Chip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';
import ClearIcon from '@material-ui/icons/Clear';
import { TreeObject } from '@models';
import { LocalTreeItem } from './local-tree-item';
import { useStyles } from './local-tree-styles';
import { LocalTreeNode } from './local-tree-types';

export function LocalTreePopup<OPTION extends TreeObject>({
  anchorElRef,
  disabled,
  error,
  isExpanded,
  loading,
  getSelectedItemAncestors = () => '',
  onClickAway,
  onDelete,
  onSelect,
  onToggle,
  selectedItems,
  selectedIds,
  visibleRoots,
}: {
  anchorElRef: RefObject<HTMLDivElement>;
  disabled?: boolean;
  error: string | null;
  getSelectedItemAncestors?: (item: OPTION) => string;
  isExpanded: (id: string) => boolean;
  loading: boolean;
  onClickAway: (event: React.MouseEvent<Document, MouseEvent>) => void;
  onDelete: (item: OPTION) => void;
  onSelect: (node: LocalTreeNode<OPTION>) => void;
  onToggle: (id: string) => void;
  selectedItems: OPTION[];
  selectedIds: Set<string>;
  visibleRoots: LocalTreeNode<OPTION>[];
}) {
  const classes = useStyles();

  return (
    <Popper
      className={classes.popper}
      role="presentation"
      anchorEl={anchorElRef.current}
      placement="bottom-start"
      open={true}
    >
      <ClickAwayListener onClickAway={onClickAway}>
        <Paper className={classes.paper} square={true} elevation={8}>
          <div className={classes.popupLayout}>
            <div className={classes.resultsColumn}>
              <Typography className={classes.columnTitle}>K výběru</Typography>
              {loading && (
                <Typography className={classes.emptyState}>
                  Načítání...
                </Typography>
              )}
              {!loading && error && (
                <Typography className={classes.emptyState}>{error}</Typography>
              )}
              {!loading && !error && visibleRoots.length === 0 && (
                <Typography className={classes.emptyState}>
                  Nebyl nalezen žádný výsledek.
                </Typography>
              )}
              {!loading &&
                !error &&
                renderTreeItems(visibleRoots, {
                  disabled,
                  isExpanded,
                  onSelect,
                  onToggle,
                  selectedIds,
                })}
            </div>
            <div className={classes.selectedColumn}>
              <Typography className={classes.columnTitle}>Vybráno</Typography>
              {selectedItems.length === 0 ? (
                <Typography className={classes.emptyState}>
                  Zatím nic není vybráno.
                </Typography>
              ) : (
                <div className={classes.selectedChips}>
                  {selectedItems.map((item) => {
                    const ancestors = getSelectedItemAncestors(item);

                    return (
                      <div key={item.id} className={classes.chipWithAncestors}>
                        <Chip
                          label={item.label}
                          className={classes.chip}
                          variant="outlined"
                          size="small"
                          icon={
                            <div
                              style={{
                                cursor: 'pointer',
                                fontSize: 'small',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(item);
                              }}
                            >
                              <ClearIcon style={{ fontSize: 'small' }} />
                            </div>
                          }
                        />
                        {ancestors && (
                          <span className={classes.chipAncestors}>
                            {ancestors}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

export function renderTreeItems<OPTION extends TreeObject>(
  nodes: LocalTreeNode<OPTION>[],
  props: {
    disabled?: boolean;
    isExpanded: (id: string) => boolean;
    onSelect: (node: LocalTreeNode<OPTION>) => void;
    onToggle: (id: string) => void;
    selectedIds: Set<string>;
  }
): React.ReactNode {
  return nodes.map((node) => (
    <React.Fragment key={node.id}>
      <LocalTreeItem
        node={node}
        disabled={props.disabled}
        isExpanded={props.isExpanded(node.id)}
        onSelect={props.onSelect}
        onToggle={props.onToggle}
        selected={props.selectedIds.has(node.id)}
      />
      {node.children.length > 0 && renderTreeItems(node.children, props)}
    </React.Fragment>
  ));
}
